import { supabase } from '../lib/supabase';
import { Sketch, UserProfile } from '../types';

/**
 * Converts a Data URL (base64) to a Blob for uploading
 */
const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const res = await fetch(dataUrl);
  return await res.blob();
};

/**
 * Get user profile (credits)
 */
export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data as UserProfile;
  } catch (error) {
    console.error('Error fetching profile:', error);
    return null;
  }
};

/**
 * Deduct 1 credit from user securely via RPC
 */
export const deductCredit = async (userId: string, currentCredits: number): Promise<boolean> => {
  try {
    // Use RPC call for secure deduction
    const { data, error } = await supabase.rpc('decrement_credits', {
      row_id: userId
    });

    if (error) throw error;
    return data as boolean;
  } catch (error) {
    console.error('Error deducting credit:', error);
    return false;
  }
};

/**
 * Uploads the image to Supabase Storage and saves metadata to the 'sketches' table
 */
export const saveSketchToHistory = async (
  userId: string,
  dataUrl: string,
  concept: string,
  prompt: string
): Promise<Sketch | null> => {
  try {
    // 1. Upload Image to Storage
    const blob = await dataUrlToBlob(dataUrl);
    // Create a unique path: user_id/timestamp.jpg
    const filePath = `${userId}/${Date.now()}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from('sketches')
      .upload(filePath, blob, {
        contentType: 'image/jpeg',
      });

    if (uploadError) throw uploadError;

    // 2. Get Public URL
    const { data: { publicUrl } } = supabase.storage
      .from('sketches')
      .getPublicUrl(filePath);

    // 3. Insert Record into DB
    const { data, error: dbError } = await supabase
      .from('sketches')
      .insert([
        {
          user_id: userId,
          image_url: publicUrl,
          concept: concept,
          prompt: prompt,
        },
      ])
      .select()
      .single();

    if (dbError) throw dbError;

    return data as Sketch;
  } catch (error) {
    console.error('Error saving sketch:', error);
    return null;
  }
};

/**
 * Fetches all sketches for the current user
 */
export const getUserSketches = async (): Promise<Sketch[]> => {
  try {
    const { data, error } = await supabase
      .from('sketches')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Sketch[];
  } catch (error) {
    console.error('Error fetching sketches:', error);
    return [];
  }
};

/**
 * Deletes a sketch from both the database and the storage bucket
 */
export const deleteSketch = async (sketchId: string, imageUrl: string): Promise<boolean> => {
  try {
    // 1. Delete from Database
    const { error: dbError } = await supabase
      .from('sketches')
      .delete()
      .eq('id', sketchId);

    if (dbError) throw dbError;

    // 2. Delete from Storage
    // Extract the file path from the public URL
    // URL format: .../storage/v1/object/public/sketches/USER_ID/FILE.jpg
    const urlParts = imageUrl.split('/sketches/');
    if (urlParts.length === 2) {
      const filePath = urlParts[1];
      const { error: storageError } = await supabase.storage
        .from('sketches')
        .remove([filePath]);
      
      if (storageError) console.error('Error removing file from bucket:', storageError);
    }

    return true;
  } catch (error) {
    console.error('Error deleting sketch:', error);
    return false;
  }
};